import { Capacitor } from "@capacitor/core";
import type {
	PurchasesOfferings,
	PurchasesPackage,
} from "@revenuecat/purchases-capacitor";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import type { Swiper as SwiperType } from "swiper";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";

import { Header } from "../components/Header";
import {
	type BillingPeriod,
	BillingToggle,
} from "../components/pricing/BillingToggle";
import { PlanTabs, type PlanType } from "../components/pricing/PlanTabs";
import { PricingCard } from "../components/pricing/PricingCard";
import { PLAN_FEATURES } from "../constants/pricingPlans";
import { useAnalytics } from "../lib/analytics";
import { useClientTranslations } from "../lib/i18n/client";
import * as RevenueCatService from "../services/revenueCatService";
import styles from "./Pricing.module.css";

export function Pricing() {
	const { t } = useTranslation("pricing");
	const { t: tCommon } = useClientTranslations("common");
	const [activePlan, setActivePlan] = useState<PlanType>("plus");
	const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("monthly");
	const [offerings, setOfferings] = useState<PurchasesOfferings | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isPurchasing, setIsPurchasing] = useState(false);
	const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
	const [userEntitlements, setUserEntitlements] = useState<{
		plus: boolean;
		premium: boolean;
		pro: boolean;
	}>({ plus: false, premium: false, pro: false });
	const { logEvent } = useAnalytics();
	const navigate = useNavigate();

	// Track page view
	useEffect(() => {
		logEvent("pricing_page_viewed");
	}, [logEvent]);

	// Fetch user entitlements
	useEffect(() => {
		async function loadUserEntitlements() {
			try {
				const customerInfo = await RevenueCatService.getCustomerInfo();
				const activeEntitlements = customerInfo.entitlements.active || {};

				const entitlements = {
					plus: "plus" in activeEntitlements,
					premium: "premium" in activeEntitlements,
					pro: "pro" in activeEntitlements,
				};

				console.log("🔐 User entitlements loaded:", entitlements);
				console.log("🔐 Active entitlements:", activeEntitlements);
				setUserEntitlements(entitlements);
			} catch (error) {
				console.error("Failed to load user entitlements:", error);
				// Keep default (all false)
			}
		}

		loadUserEntitlements();
	}, []);

	// Fetch offerings from RevenueCat
	useEffect(() => {
		async function loadOfferings() {
			try {
				setIsLoading(true);

				// Add a small delay to allow SDK to initialize
				// This gives UserDataLoader time to configure RevenueCat
				await new Promise((resolve) => setTimeout(resolve, 500));

				const data = await RevenueCatService.getOfferings();
				setOfferings(data as PurchasesOfferings | null);
				logEvent("pricing_offerings_loaded", undefined, {
					platform: Capacitor.getPlatform(),
				});
			} catch (error) {
				console.error("Failed to load offerings:", error);
				logEvent("pricing_offerings_failed", undefined, {
					platform: Capacitor.getPlatform(),
					error: (error as Error)?.message,
				});
				// Don't show error toast on web - will use mock pricing as fallback
				const platform = Capacitor.getPlatform();
				if (platform !== "web") {
					toast.error(t("errors.loadFailed"));
				}
			} finally {
				setIsLoading(false);
			}
		}

		loadOfferings();
	}, [t, logEvent]);

	// Sync swiper with active plan
	useEffect(() => {
		if (swiperInstance) {
			const planIndex = ["core", "plus", "premium"].indexOf(activePlan);
			swiperInstance.slideTo(planIndex);
		}
	}, [activePlan, swiperInstance]);

	const handlePlanChange = (plan: PlanType) => {
		setActivePlan(plan);
		logEvent("pricing_plan_selected", undefined, { plan });
	};

	const handleBillingPeriodChange = (period: BillingPeriod) => {
		setBillingPeriod(period);
		logEvent("pricing_billing_toggled", undefined, { period });
	};

	const handleSlideChange = (swiper: SwiperType) => {
		const plans: PlanType[] = ["core", "plus", "premium"];
		const newPlan = plans[swiper.activeIndex];
		setActivePlan(newPlan);
		logEvent("pricing_plan_swiped", undefined, { plan: newPlan });
	};

	const handleChoosePlan = async (packageData: PurchasesPackage) => {
		try {
			setIsPurchasing(true);

			// Handle Core (free) plan - open subscription management to cancel
			if (packageData.identifier === "core_free") {
				logEvent("pricing_cancel_subscription_initiated");

				const managementURL = await RevenueCatService.getManagementURL();

				if (managementURL) {
					// Open the subscription management page
					window.open(managementURL, "_blank");
					toast.success(t("messages.managementOpened"));
					logEvent("pricing_management_opened");
				} else {
					toast.error(t("errors.managementURLNotFound"));
					logEvent("pricing_management_error");
				}

				setIsPurchasing(false);
				return;
			}

			// Extract product identifier - handle both native and web SDK formats
			const webPackage = packageData as unknown as {
				rcBillingProduct?: { identifier: string };
				webBillingProduct?: { identifier: string };
			};
			const nativePackage = packageData as unknown as {
				product?: { identifier: string };
			};

			const productId =
				webPackage.rcBillingProduct?.identifier ||
				webPackage.webBillingProduct?.identifier ||
				nativePackage.product?.identifier ||
				packageData.identifier;

			logEvent("pricing_purchase_initiated", undefined, {
				plan: activePlan,
				billing: billingPeriod,
				productId: productId,
			});

			const result = await RevenueCatService.purchasePackage(packageData);

			if (result.success) {
				toast.success(tCommon("toast.success.subscriptionActivated"));
				logEvent("pricing_purchase_completed", undefined, {
					plan: activePlan,
					billing: billingPeriod,
				});

				// Navigate to profile or success page
				setTimeout(() => {
					navigate("/profile");
				}, 1500);
			}
		} catch (error: unknown) {
			console.error("Purchase failed:", error);

			// Handle user cancellation gracefully
			const errorObj = error as { code?: string; message?: string };
			if (errorObj?.code === "1" || errorObj?.message?.includes("cancelled")) {
				logEvent("pricing_purchase_cancelled", undefined, { plan: activePlan });
				// Don't show error for cancellation
				return;
			}

			toast.error(t("errors.purchaseFailed"));
			logEvent("pricing_purchase_failed", undefined, {
				plan: activePlan,
				error: errorObj?.message,
			});
		} finally {
			setIsPurchasing(false);
		}
	};

	// Get package for current selection (with web fallback)
	const getCurrentPackage = (plan: PlanType): PurchasesPackage | null => {
		const platform = Capacitor.getPlatform();
		const packageType = billingPeriod === "yearly" ? "annual" : "monthly";

		// Core plan is always free
		if (plan === "core") {
			return {
				identifier: "core_free",
				packageType: "CUSTOM",
				product: {
					identifier: "core_free",
					priceString: "$0",
					title: t("plans.core.name"),
					description: t("messages.freeDescription"),
				},
			} as PurchasesPackage;
		}

		if (platform === "web") {
			// Try to use real web offerings if available
			if (offerings) {
				// Map plan to offering identifier based on billing period
				// For monthly: "plus" -> "web-offering", "premium" -> "web_premium"
				// For annual: "plus" -> "plus_annual", "premium" -> "premium_annual"
				let targetOfferingId = "";
				if (plan === "plus") {
					targetOfferingId =
						packageType === "annual" ? "plus_annual" : "web-offering";
				} else if (plan === "premium") {
					targetOfferingId =
						packageType === "annual" ? "premium_annual" : "web_premium";
				}

				// Get the offering for this plan
				const offering = offerings.all?.[targetOfferingId] || offerings.current;

				if (
					offering?.availablePackages &&
					offering.availablePackages.length > 0
				) {
					// For monthly/annual plans, look for the appropriate package
					let foundPackage = null;

					if (packageType === "monthly") {
						foundPackage = offering.monthly || offering.availablePackages[0];
					} else if (packageType === "annual") {
						foundPackage = offering.annual || offering.availablePackages[0];
					}

					if (foundPackage) {
						return foundPackage;
					}
				}
			}

			// Fallback to mock data if offerings not loaded or package not found
			const mockPrices = {
				plus: { monthly: "$12", yearly: "$115" },
				premium: { monthly: "$39", yearly: "$370" },
			};

			const price =
				packageType === "annual"
					? mockPrices[plan].yearly
					: mockPrices[plan].monthly;
			const packageTypeEnum = packageType === "annual" ? "ANNUAL" : "MONTHLY";
			const periodLabel = packageType === "annual" ? "year" : "month";

			return {
				identifier: `${plan}_${packageType}`,
				packageType: packageTypeEnum,
				product: {
					identifier: `${plan}_${packageType}`,
					priceString: price,
					title: `${t(`plans.${plan}.name`)} - ${t(`billing.${periodLabel}`)}`,
					description: `${t("buttons.choosePlan")} ${t(`plans.${plan}.name`)}`,
				},
			} as PurchasesPackage;
		}

		// For native platforms, use real RevenueCat data with same offering IDs as web
		let targetOfferingId = "";
		if (plan === "plus") {
			targetOfferingId =
				packageType === "annual" ? "plus_annual" : "web-offering";
		} else if (plan === "premium") {
			targetOfferingId =
				packageType === "annual" ? "premium_annual" : "web_premium";
		}

		// Get the specific offering for this plan
		const offering = offerings?.all?.[targetOfferingId] || offerings?.current;
		if (!offering) return null;

		const packages = offering.availablePackages || [];

		// For native, try to find package by type (monthly/annual)
		let foundPackage = null;
		if (packageType === "monthly") {
			foundPackage =
				offering.monthly ||
				packages.find((pkg: PurchasesPackage) => pkg.packageType === "MONTHLY");
		} else if (packageType === "annual") {
			foundPackage =
				offering.annual ||
				packages.find((pkg: PurchasesPackage) => pkg.packageType === "ANNUAL");
		}

		return foundPackage || packages[0] || null;
	};

	if (isLoading) {
		return (
			<div className={styles.wrapper}>
				<Header />
				<main className={styles.main}>
					<div className={styles.loading}>
						<div className={styles.spinner} />
						<p>{t("messages.loading")}</p>
					</div>
				</main>
			</div>
		);
	}

	const plans: PlanType[] = ["core", "plus", "premium"];

	return (
		<div className={styles.wrapper}>
			<Header />
			<main className={styles.main}>
				<div className={styles.container}>
					<div className={styles.header}>
						<h1 className={styles.title}>{t("page.title")}</h1>
						<h2 className={styles.subtitle}>{t("page.subtitle")}</h2>
					</div>

					<PlanTabs activePlan={activePlan} onPlanChange={handlePlanChange} />

					<BillingToggle
						period={billingPeriod}
						onPeriodChange={handleBillingPeriodChange}
					/>

					<div className={styles.carouselContainer}>
						<Swiper
							modules={[Pagination]}
							spaceBetween={20}
							slidesPerView={1}
							centeredSlides={true}
							pagination={{ clickable: true }}
							onSwiper={setSwiperInstance}
							onSlideChange={handleSlideChange}
							initialSlide={1} // Start with "plus" plan
							className={styles.swiper}
						>
							{plans.map((plan) => {
								const packageData = getCurrentPackage(plan);
								if (!packageData) return null;

								// Check if user has this plan
								// Core is current plan only if user has no paid subscriptions
								const hasPaidSubscription =
									userEntitlements.plus || userEntitlements.premium;

								let isCurrentPlan = false;
								if (plan === "core") {
									isCurrentPlan = !hasPaidSubscription;
								} else if (plan === "plus") {
									isCurrentPlan = userEntitlements.plus;
								} else if (plan === "premium") {
									isCurrentPlan = userEntitlements.premium;
								}

								console.log(`📦 Plan ${plan}:`, {
									isCurrentPlan,
									userEntitlements,
								});

								return (
									<SwiperSlide key={plan}>
										<PricingCard
											packageData={packageData}
											planName={t(`plans.${plan}.name`)}
											features={PLAN_FEATURES[plan]}
											isPopular={plan === "plus"}
											onChoosePlan={handleChoosePlan}
											isLoading={isPurchasing}
											isCurrentPlan={isCurrentPlan}
											userHasSubscription={hasPaidSubscription}
										/>
									</SwiperSlide>
								);
							})}
						</Swiper>
					</div>

					<p className={styles.disclaimer}>{t("messages.disclaimer")}</p>
				</div>
			</main>
		</div>
	);
}
